import { BillStatus } from '../../__generated__/globalTypes';
import { getRelativeDateBucket, pluralize } from '../formatting';

import type { RelativeDateBucket } from '../formatting';
import type { CalendarDate } from '@internationalized/date';
import type { BadgeColors } from '@orn/shared-web/components/Badge';

export type TimingPillVariant = 'bill' | 'recurring';

export interface ITimingPill {
  label: string;
  color: BadgeColors;
}

export interface ITimingPillOptions {
  /** @default 'bill' */
  variant?: TimingPillVariant;
  /** A bill's status, which outranks its due date once it is paid. */
  status?: BillStatus;
}

function billTiming(bucket: RelativeDateBucket): ITimingPill {
  if (bucket.direction === 'today') {
    return { label: 'Due today', color: 'warning' };
  }
  if (bucket.direction === 'past') {
    return { label: `${pluralize(bucket.value, bucket.unit)} overdue`, color: 'error' };
  }
  if (bucket.value === 1 && bucket.unit === 'day') {
    return { label: 'Due tomorrow', color: 'brand' };
  }
  return { label: `Due in ${pluralize(bucket.value, bucket.unit)}`, color: 'brand' };
}

function recurringTiming(bucket: RelativeDateBucket): ITimingPill {
  if (bucket.direction === 'today') {
    return { label: 'Expected today', color: 'warning' };
  }
  if (bucket.direction === 'past') {
    return { label: 'Past expected date', color: 'error' };
  }
  const label =
    bucket.value === 1 && bucket.unit === 'day'
      ? 'Expected tomorrow'
      : `Expected in ${pluralize(bucket.value, bucket.unit)}`;
  return { label, color: 'brand' };
}

const STATUS_OVERRIDES: Partial<Record<BillStatus, ITimingPill>> = {
  [BillStatus.Paid]: { label: 'Paid', color: 'success' },
  [BillStatus.PartiallyPaid]: { label: 'Partially paid', color: 'warning' },
};

/**
 * The hero pill for a due or expected date: a settled bill status wins (Paid,
 * Partially paid), otherwise the date's distance from today — "Due in 3 days"
 * for a bill, "Expected in 3 days" for a recurring payment.
 */
export function getTimingPill(date: CalendarDate, { variant = 'bill', status }: ITimingPillOptions = {}): ITimingPill {
  const override = status ? STATUS_OVERRIDES[status] : undefined;
  if (override) {
    return override;
  }
  const bucket = getRelativeDateBucket(date);
  return variant === 'bill' ? billTiming(bucket) : recurringTiming(bucket);
}
